import * as React from 'react';
import { CalendarIcon } from 'lucide-react';
import { addYears, isValid, parse, startOfMonth, subYears } from 'date-fns';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface DatePickerProps {
  value?: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  minDate?: Date;
  maxDate?: Date;
  /**
   * Month the calendar opens on while the field is still empty — an anchor, not
   * a constraint. `minDate` doubles as one (it drives `startMonth` below), so a
   * paired field that gives up the constraint to report an out-of-order pair
   * rather than silently refuse it would otherwise send the user back to the
   * current month. Ignored from the moment the field first holds a value, or
   * the user navigates the calendar themselves — the month they were last left
   * on is theirs, and clearing the field afterwards does not hand it back.
   */
  anchorMonth?: Date | null;
  /**
   * Receives the text input. The picker keeps its own reference either way, so
   * this hands out the same node rather than replacing anything — a caller that
   * has to move focus here, such as a form putting the caret on the first field
   * it is complaining about, should not have to find the element by id and miss
   * it in silence if the id ever stops reaching the input.
   */
  inputRef?: React.RefObject<HTMLInputElement | null>;
  /** Marks the field as invalid (destructive border + aria-invalid) — used when a form validation message points at this field. */
  invalid?: boolean;
  /** Id of the caller-rendered error message, wired via aria-describedby while invalid. */
  errorId?: string;
}

/**
 * The committed value as the box shows it — and the width a caller has to leave
 * room for. The widest rendering is "September 06, 6666", not today's date:
 * Poppins sets its digits proportionally and `6` is the widest of them, so the
 * longest month carrying the widest digits is what a fixed box has to hold. It
 * measures 141.4px at 14px and 161.6px at 16px (Chrome canvas `measureText`,
 * 2026-10-15 — the same method gives back the 135.8px and 155.2px Geist measured
 * in the DOM before it), and both sizes are live: the input is
 * `text-base md:text-sm` (ui/input.tsx), the larger one below the `md`
 * breakpoint and the smaller at or above it. Add the 12px of left padding, the
 * 40px of right padding that keeps text clear of the calendar button, and the
 * input's own 1px border on each side, and a caller pinning a fixed width needs
 * 196px to hold the 14px value and 216px to hold the 16px one; the 16px floor is
 * exact in the DOM (Reports' custom range: 215 clips, 216 holds). The custom-range
 * toolbars pin 220px, since a width sitting on an exact floor leaves nothing for
 * a rendering difference between engines and their rows wrap rather than
 * overflow. [HW-927]
 *
 * Both floors assume a 16px root. Tailwind sizes text in rem — `md` among them,
 * so that breakpoint is 48rem rather than a flat 768px — while these widths are
 * px, so a browser set to a larger default font scales the value past the box and
 * clips the year again. That is the app's bargain rather than this box's: every
 * width on these toolbars is px. [HW-881]
 */
function formatDate(date: Date | undefined | null): string {
  if (!date) {
    return '';
  }
  return date.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

/** Smallest year the box accepts — see {@link parseTypedDate}. */
const MIN_TYPED_YEAR = 1000;

/**
 * Text formats accepted from the box, tried in order.
 *
 * Every entry must be *prefix-safe*: no proper prefix of a date being typed may
 * match it, or the half-typed text would commit a day the user never wrote. The
 * `MIN_TYPED_YEAR` floor is what buys that here — each format ends in `yyyy`, and
 * date-fns' `yyyy` accepts 1–4 digits, so a year still being typed ("8/20/2",
 * "8/20/20") parses to year 2 or 20 and is refused. A two-digit-year pattern
 * ('M/d/yy') cannot be added for the same reason: it would accept those prefixes
 * as 2002 and 2020.
 */
const TYPED_DATE_FORMATS = ['MMMM d, yyyy', 'MMMM d yyyy', 'd MMMM yyyy', 'M/d/yyyy'];

/**
 * Parse what the user typed into a LOCAL-midnight `Date`, or null when the text is
 * not (yet) a complete calendar day.
 *
 * `new Date(text)` cannot be used for this: it accepts partial input ("2026" → 1
 * January, "2026-08" → 1 August), so every keystroke of a date being typed committed
 * a different day — and since the value effect below re-formats the box from the
 * committed value, the text was rewritten under the caret mid-typing. It also reads
 * the "YYYY-MM-DD" form as UTC midnight, which is the previous calendar day for
 * viewers west of UTC, while the calendar yields local midnight; the two bases must
 * agree because callers serialise the result with local getters (`toDateParam`).
 */
function parseTypedDate(text: string): Date | null {
  const trimmed = text.trim();
  if (!trimmed) {
    return null;
  }

  // Built directly as a local calendar day. Requiring 2-digit month and day also
  // keeps the half-typed "2026-08-2" from committing as 2 August.
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (iso) {
    const [year, month, day] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
    // `new Date(year, …)` remaps years 0–99 onto 1900–1999, so "0020-08-20" would
    // silently become 1920 — refuse the whole sub-1000 range here as well.
    if (year < MIN_TYPED_YEAR) {
      return null;
    }
    const date = new Date(year, month - 1, day);
    // Rejects impossible days — "2026-02-31" would otherwise roll into March.
    return date.getMonth() === month - 1 && date.getDate() === day ? date : null;
  }

  // date-fns matches en-US *narrow* month names too, where a single letter is a
  // whole month ("A" → April, "J" → January), so a month word being retyped would
  // commit the wrong month before it is finished. Nothing shorter than the
  // 3-letter abbreviation counts.
  if (/[a-z]/i.test(trimmed) && !/[a-z]{3}/i.test(trimmed)) {
    return null;
  }

  const now = new Date();
  for (const dateFormat of TYPED_DATE_FORMATS) {
    const parsed = parse(trimmed, dateFormat, now);
    if (!isValid(parsed)) {
      continue;
    }
    // A year below the floor is a year still being typed, not one the user means.
    return parsed.getFullYear() >= MIN_TYPED_YEAR ? parsed : null;
  }

  return null;
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'Pick a date',
  disabled = false,
  className,
  id,
  minDate,
  maxDate,
  anchorMonth,
  inputRef: callerInputRef,
  invalid = false,
  errorId,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [month, setMonth] = React.useState<Date | undefined>(value || undefined);
  const [inputValue, setInputValue] = React.useState(formatDate(value));
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Both references point at the same node: the picker needs its own to tell
  // whether the box has focus (see the effect below), and the caller's is what
  // lets it reach the input without going through the DOM by id. Memoised
  // because React re-runs a callback ref — detaching with null, then attaching
  // again — whenever its identity changes, which for an inline arrow would be
  // every render of every one of this component's call sites.
  const attachInput = React.useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (callerInputRef) callerInputRef.current = node;
    },
    [callerInputRef]
  );

  React.useEffect(() => {
    // Never re-format the text the user is in the middle of: editing a date in
    // place passes through complete dates ("August 2, 2026" while changing the day
    // of "August 20, 2026"), and rewriting the box on that commit moves the caret
    // to the end mid-word. While the box has focus its text is the user's; blur
    // re-syncs it from the committed value.
    if (document.activeElement !== inputRef.current) {
      setInputValue(formatDate(value));
    }
    if (value) {
      setMonth(value);
    }
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.target.value;
    setInputValue(inputVal);

    if (!inputVal) {
      onChange(null);
      return;
    }

    const date = parseTypedDate(inputVal);
    // Incomplete or unrecognised text leaves the committed value untouched — it is
    // re-synced on blur — so a date being typed is never partially committed.
    if (!date) return;
    // Check min/max constraints
    if (minDate && date < minDate) return;
    if (maxDate && date > maxDate) return;
    onChange(date);
    setMonth(date);
  };

  // Whatever the box is left with — half-typed, unrecognised, or a date outside the
  // allowed range, none of which commit — snaps back to the value the form actually
  // holds, so the visible date and the value that will be submitted cannot diverge.
  const handleInputBlur = () => {
    setInputValue(formatDate(value));
  };

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      onChange(date);
      setInputValue(formatDate(date));
      setMonth(date);
    } else {
      onChange(null);
      setInputValue('');
    }
    setOpen(false);
  };

  // Navigable month range for the calendar (drives the year dropdown too).
  // Without an explicit range, react-day-picker's `dropdown` caption is pinned
  // to the current year, so dates in a later year (e.g. a leave request crossing
  // a year boundary) can't be reached even though `disabled` would allow them.
  // Bound to [minDate, maxDate] when provided; otherwise a wide fallback so
  // unbounded callers (e.g. birthdates, HR with no limit) can still navigate.
  const startMonth = React.useMemo(
    () => startOfMonth(minDate ?? subYears(new Date(), 120)),
    [minDate]
  );
  const endMonth = React.useMemo(
    () => startOfMonth(maxDate ?? addYears(new Date(), 10)),
    [maxDate]
  );

  return (
    <div className={cn('relative flex gap-2', className)}>
      <Input
        ref={attachInput}
        id={id}
        value={inputValue}
        placeholder={placeholder}
        className={cn(
          'bg-background pr-10',
          invalid && 'border-destructive focus-visible:ring-destructive'
        )}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid && errorId ? errorId : undefined}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        disabled={disabled}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            className="absolute top-1/2 right-2 size-6 -translate-y-1/2"
            disabled={disabled}
          >
            <CalendarIcon className="size-3.5" />
            <span className="sr-only">Select date</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto overflow-hidden p-0"
          align="end"
          alignOffset={-8}
          sideOffset={10}
        >
          <Calendar
            mode="single"
            selected={value || undefined}
            captionLayout="dropdown"
            startMonth={startMonth}
            endMonth={endMonth}
            // `month` is set from the value and by the user's own navigation;
            // until either happens the anchor decides, and after either one it
            // is the user's place in the calendar that wins.
            month={month ?? anchorMonth ?? undefined}
            onMonthChange={setMonth}
            onSelect={handleSelect}
            initialFocus
            disabled={(date) => {
              if (minDate && date < minDate) return true;
              if (maxDate && date > maxDate) return true;
              return false;
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
